/**
 * Shared skin presets for the shim. Every color resolves to a design token, so
 * the Phase 8 design profile lands here rather than in ~190 page files.
 */

/**
 * Status colors used by Tag, Badge, Alert, Progress, Timeline, Steps.
 *
 * Text uses the `-ink` token rather than the base colour: these chips put the
 * status colour on its own 10% tint, where a bright fill colour would not reach
 * AA as text.
 */
export const STATUS_CLASSES = {
  success: 'bg-[var(--success-green-bg)] text-[var(--success-green-ink)] border-[var(--success-green-border)]',
  processing: 'bg-[var(--info-blue-bg)] text-[var(--info-blue-ink)] border-[var(--info-blue-border)]',
  error: 'bg-[var(--error-bg)] text-[var(--error-ink)] border-[var(--error-border)]',
  warning: 'bg-[var(--warning-bg)] text-[var(--warning-ink)] border-[var(--warning-border)]',
  default: 'bg-[var(--gray-100)] text-[var(--text-body)] border-[var(--border-color)]',
  info: 'bg-[var(--info-blue-bg)] text-[var(--info-blue-ink)] border-[var(--info-blue-border)]',
}

/** antd's named preset colors for Tag and Badge. */
export const PRESET_COLORS = {
  magenta: '#c41d7f',
  red: '#cf1322',
  volcano: '#d4380d',
  orange: '#d46b08',
  gold: '#d48806',
  lime: '#7cb305',
  green: '#389e0d',
  cyan: '#08979c',
  blue: '#0958d9',
  geekblue: '#1d39c4',
  purple: '#531dab',
  pink: '#c41d7f',
  default: 'var(--text-body)',
  processing: 'var(--info-blue)',
  success: 'var(--success-green)',
  error: 'var(--error-color)',
  warning: 'var(--warning-color)',
}

export const PRESET_BACKGROUNDS = {
  magenta: '#fff0f6',
  red: '#fff1f0',
  volcano: '#fff2e8',
  orange: '#fff7e6',
  gold: '#fffbe6',
  lime: '#fcffe6',
  green: '#f6ffed',
  cyan: '#e6fffb',
  blue: '#e6f4ff',
  geekblue: '#f0f5ff',
  purple: '#f9f0ff',
  pink: '#fff0f6',
}

export const PRESET_BORDERS = {
  magenta: '#ffadd2',
  red: '#ffa39e',
  volcano: '#ffbb96',
  orange: '#ffd591',
  gold: '#ffe58f',
  lime: '#eaff8f',
  green: '#b7eb8f',
  cyan: '#87e8de',
  blue: '#91caff',
  geekblue: '#adc6ff',
  purple: '#d3adf7',
  pink: '#ffadd2',
}

export const isPresetColor = (color) =>
  typeof color === 'string' && Object.hasOwn(PRESET_COLORS, color)

/** Resolves antd's `color` prop into inline skin styles. */
export const resolvePresetStyle = (color) => {
  if (!color) return null
  if (isPresetColor(color)) {
    return {
      color: PRESET_COLORS[color],
      backgroundColor: PRESET_BACKGROUNDS[color] || 'var(--gray-100)',
      borderColor: PRESET_BORDERS[color] || 'var(--border-color)',
    }
  }
  // Arbitrary hex/rgb: antd fills the chip and uses white text.
  return { color: '#ffffff', backgroundColor: color, borderColor: color }
}

export const SURFACE = 'bg-[var(--surface-card)]'
export const BORDER = 'border-[var(--border-color)]'
export const TEXT_BODY = 'text-[var(--text-body)]'
export const TEXT_MUTED = 'text-[var(--text-muted)]'
export const TEXT_HEADING = 'text-[var(--text-heading)]'
export const RADIUS = 'rounded-[var(--radius-sm)]'
export const RADIUS_MD = 'rounded-[var(--radius-md)]'
/**
 * Buttons, inputs, selects, pickers, badges and tooltips all sit on the same step
 * in the reference (`rounded-md`), and so do its popovers and dropdowns — the
 * aliases exist to record intent, not because the values differ.
 */
export const RADIUS_CONTROL = 'rounded-[var(--radius-sm)]'
export const RADIUS_PANEL = 'rounded-[var(--radius-sm)]'
/** Menu, dropdown and select items. */
export const RADIUS_ITEM = 'rounded-[var(--radius-xs)]'
/** Cards. */
export const RADIUS_CARD = 'rounded-[var(--radius-lg)]'

/**
 * The field surface: an unfilled box with a hairline and a small drop shadow.
 * Kept as a class in index.css so focus can set both the border and the outline.
 */
export const FIELD_SURFACE = 'ui-input-base'
/**
 * The reference's ring is two pixels in `--ring` with a two-pixel offset, applied
 * globally in index.css on `:focus-visible`. This keeps a matching ring available
 * to components that need it inline, and no longer suppresses the outline.
 */
export const FOCUS_RING =
  'focus-visible:ring-2 focus-visible:ring-[var(--field-ring)] focus-visible:ring-offset-2'
/** Minimum touch target, sized by the active profile's control height. */
export const TOUCH_TARGET = 'min-h-[var(--control-height)]'

/**
 * Label treatment for form labels, section headings and table column headers.
 * The reference's is plain: body size, medium weight, no uppercase and no
 * tracking. Defined as a class in index.css so it stays in one place.
 */
export const LABEL = 'ui-label'

/**
 * Field padding. `middle` is the reference's `px-3 py-1`; buttons use the wider
 * BUTTON_SIZES below, which is the one place the two diverge.
 */
export const CONTROL_SIZES = {
  small: 'px-2.5 py-1 text-xs',
  middle:
    'px-[var(--control-padding-x)] py-[var(--control-padding-y)] text-sm',
  large: 'px-4 py-2 text-base',
}

/** `h-8 px-3 text-xs`, `h-9 px-4 py-2 text-sm`, `h-10 px-8 text-sm`. */
export const BUTTON_SIZES = {
  small: 'min-h-[var(--control-height-sm)] px-[var(--button-padding-x-sm)] text-xs',
  middle: 'min-h-[var(--control-height)] px-[var(--button-padding-x)] py-2 text-sm',
  large: 'min-h-[var(--control-height-lg)] px-[var(--button-padding-x-lg)] text-sm',
}

export const STATUS_BORDER = {
  error: 'border-[var(--error-color)]',
  warning: 'border-[var(--warning-color)]',
  default: BORDER,
}

/**
 * Status classes for fields wearing FIELD_SURFACE. These are plain class names,
 * not `border-*` utilities: index.css resolves them with a two-class selector so
 * they beat the `border` shorthand that `.ui-input-base` sets.
 */
export const STATUS_FIELD = {
  error: 'ui-input-status-error',
  warning: 'ui-input-status-warning',
}
